import { createContext, useContext, useState, ReactNode } from 'react';

// Common timezone options for North America (can be expanded)
export const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: 'UTC-5/-4' },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: 'UTC-6/-5' },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: 'UTC-7/-6' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: 'UTC-8/-7' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)', offset: 'UTC-9/-8' },
  { value: 'America/Edmonton', label: 'Edmonton (MT)', offset: 'UTC-7/-6' },
  { value: 'America/Vancouver', label: 'Vancouver (PT)', offset: 'UTC-8/-7' },
  { value: 'America/Toronto', label: 'Toronto (ET)', offset: 'UTC-5/-4' },
  { value: 'America/Halifax', label: 'Atlantic Time (AT)', offset: 'UTC-4/-3' },
  { value: 'UTC', label: 'UTC', offset: 'UTC+0' },
];

const STORAGE_KEY = 'cdr-dashboard-timezone';
const DEFAULT_TIMEZONE = 'America/New_York';

function isValidTimezone(timezone: string | null): timezone is string {
  if (!timezone) {
    return false;
  }

  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

function getInitialTimezone(): string {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (isValidTimezone(stored)) {
    return stored;
  }

  try {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (isValidTimezone(browserTz)) {
      return browserTz;
    }
  } catch {
    // Fall through to app default.
  }

  return DEFAULT_TIMEZONE;
}

interface TimezoneContextType {
  timezone: string;
  setTimezone: (tz: string) => void;
  formatDateTime: (dateStr: string | null, options?: Intl.DateTimeFormatOptions) => string;
  formatDate: (dateStr: string | null) => string;
  formatTime: (dateStr: string | null) => string;
}

const TimezoneContext = createContext<TimezoneContextType | null>(null);

export function useTimezone() {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
}

interface TimezoneProviderProps {
  children: ReactNode;
}

export function TimezoneProvider({ children }: TimezoneProviderProps) {
  const [timezone, setTimezoneState] = useState<string>(getInitialTimezone);

  const setTimezone = (tz: string) => {
    if (!isValidTimezone(tz)) {
      return;
    }

    setTimezoneState(tz);
    localStorage.setItem(STORAGE_KEY, tz);
  };

  // Format a datetime string to the selected timezone
  const formatDateTime = (
    dateStr: string | null,
    options?: Intl.DateTimeFormatOptions
  ): string => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-US', {
        timeZone: timezone,
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...options,
      });
    } catch {
      return dateStr;
    }
  };

  // Format just the date portion
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Format just the time portion
  const formatTime = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const value: TimezoneContextType = {
    timezone,
    setTimezone,
    formatDateTime,
    formatDate,
    formatTime,
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
}
